import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import type { AlertColor } from '@mui/material/Alert';

export const api = axios.create({
  baseURL: '/api',
  timeout: 15_000,
  withCredentials: true,
});

// ── Snackbar error handler (registered by SnackbarContext) ─────────────────
type ErrorHandler = (message: string, severity?: AlertColor) => void;
let _errorHandler: ErrorHandler | null = null;
export function setApiErrorHandler(fn: ErrorHandler | null) { _errorHandler = fn; }

// ── Backend offline listener ───────────────────────────────────────────────
type OfflineListener = () => void;
let _offlineListener: OfflineListener | null = null;
export function setOfflineListener(fn: OfflineListener | null) { _offlineListener = fn; }

// ── Auth accessors (registered by AuthContext) ─────────────────────────────
type GetTokenFn = () => string | null;
type OnAuthFailureFn = () => void;
type OnTokenRefreshFn = (token: string) => void;

let _getToken: GetTokenFn | null = null;
let _onAuthFailure: OnAuthFailureFn | null = null;
let _onTokenRefresh: OnTokenRefreshFn | null = null;

export function setAuthAccessors(
  getToken: GetTokenFn,
  onFailure: OnAuthFailureFn,
  onTokenRefresh: OnTokenRefreshFn,
) {
  _getToken = getToken;
  _onAuthFailure = onFailure;
  _onTokenRefresh = onTokenRefresh;
}

// ── Request interceptor: attach access token ───────────────────────────────
api.interceptors.request.use(config => {
  const token = _getToken?.();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Bare client for silent token refresh (no interceptors — avoids loops) ──
const _refreshClient = axios.create({ baseURL: '/api', withCredentials: true, timeout: 10_000 });
let _pendingRefresh: Promise<string> | null = null;

// ── Response interceptor ───────────────────────────────────────────────────
api.interceptors.response.use(
  res => res,
  async (err: unknown) => {
    if (!axios.isAxiosError(err)) return Promise.reject(err);

    if (err.response?.status === 401) {
      const cfg = err.config as AxiosRequestConfig & { _retry?: boolean };
      if (!cfg._retry) {
        cfg._retry = true;
        try {
          if (!_pendingRefresh) {
            _pendingRefresh = _refreshClient
              .post<{ accessToken: string }>('/auth/refresh')
              .then(r => r.data.accessToken)
              .finally(() => { _pendingRefresh = null; });
          }
          const newToken = await _pendingRefresh;
          _onTokenRefresh?.(newToken);
          return api({ ...cfg, headers: { ...(cfg.headers ?? {}), Authorization: `Bearer ${newToken}` } });
        } catch {
          _onAuthFailure?.();
          return Promise.reject(err);
        }
      }
      _onAuthFailure?.();
      return Promise.reject(err);
    }

    const reqPath = err.config?.url ?? '';
    const isSocketIo = reqPath.includes('socket.io');
    const httpStatus = err.response?.status;
    // Suppress snackbars for gateway errors (502/503) — these happen when the
    // backend is starting up. React Query retries them silently.
    const isGatewayError = !httpStatus || httpStatus >= 502;

    const message: string =
      (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message ??
      (err.code === 'ECONNABORTED' ? 'Request timed out' : undefined) ??
      (err.message === 'Network Error' ? 'Cannot reach server' : undefined) ??
      err.message ??
      'An unexpected error occurred';
    if (!isSocketIo && !isGatewayError) _errorHandler?.(message, 'error');
    if (isGatewayError && !isSocketIo) _offlineListener?.();

    // Report 4xx/5xx API errors to the backend error log (fire-and-forget).
    // Use fetch() directly so this bypasses the interceptor and avoids loops.
    // Only send when a token is available — unauthenticated errors are not tracked.
    const status = err.response?.status;
    const reportToken = _getToken?.();
    if (status && status >= 400 && reportToken && !isSocketIo && !reqPath.includes('/errors/report')) {
      const errorCode = (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code ?? 'unknown';
      void fetch('/api/errors/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${reportToken}` },
        body: JSON.stringify({
          error_code: errorCode,
          http_status: status,
          method: err.config?.method?.toUpperCase() ?? null,
          path: reqPath,
          message,
        }),
      }).catch(() => {});
    }

    return Promise.reject(err);
  },
);
